"use client";

import React, { useCallback, useEffect, useState, useRef } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Link from "next/link";
import Image from "next/image";
import api, { API_BASE_URL, getImageUrl } from '@/app/lib/api';

interface SharedVideoItem {
	id: string;
	link: string;
	createdAt?: string;
	product?: {
		id: string;
		name: string;
		slug?: string;
		price?: number;
		images?: string[];
	} | null;
}

// helper to extract YouTube ID from a link
function extractYouTubeId(url: string | undefined): string | null {
	if (!url) return null;
	const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=|shorts\/)([^#\&\?]*).*/;
	const match = url.match(regExp);
	if (match && match[2].length === 11) {
		return match[2];
	}
	return null;
}

interface StoryModalProps {
	videos: SharedVideoItem[];
	startIndex: number;
	onClose: () => void;
}

const StoryModal: React.FC<StoryModalProps> = ({ videos, startIndex, onClose }) => {
	const [currentIndex, setCurrentIndex] = useState(startIndex);
	const [isMuted, setIsMuted] = useState(false);
	const videoRef = useRef<HTMLVideoElement>(null);
	const [progress, setProgress] = useState(0);

	const currentVideo = videos[currentIndex];
	const yid = extractYouTubeId(currentVideo?.link);

	const goToNext = useCallback(() => {
		setCurrentIndex((prev) => (prev + 1) % videos.length);
		setProgress(0);
	}, [videos.length]);

	const goToPrev = useCallback(() => {
		setCurrentIndex((prev) => (prev - 1 + videos.length) % videos.length);
		setProgress(0);
	}, [videos.length]);

	// Handle video/story duration and progress
	useEffect(() => {
		if (yid) {
			// YouTube video simulated progress (e.g., 15 seconds per story)
			setProgress(0);
			const duration = 15000;
			const intervalTime = 100;
			const steps = duration / intervalTime;
			let currentStep = 0;

			const timer = setInterval(() => {
				currentStep++;
				const nextProgress = (currentStep / steps) * 100;
				setProgress(nextProgress);
				if (currentStep >= steps) {
					clearInterval(timer);
					goToNext();
				}
			}, intervalTime);

			return () => clearInterval(timer);
		} else {
			// HTML5 video progress
			const video = videoRef.current;
			if (!video) return;

			const handleVideoEnd = () => {
				goToNext();
			};

			const handleTimeUpdate = () => {
				if (video.duration) {
					const nextProgress = (video.currentTime / video.duration) * 100;
					setProgress(nextProgress);
				}
			};

			video.addEventListener("ended", handleVideoEnd);
			video.addEventListener("timeupdate", handleTimeUpdate);

			// Reset video state
			setProgress(0);
			video.currentTime = 0;
			video.muted = isMuted;
			video.play().catch((e) => console.error("Autoplay was prevented:", e));

			return () => {
				video.removeEventListener("ended", handleVideoEnd);
				video.removeEventListener("timeupdate", handleTimeUpdate);
			};
		}
	}, [currentIndex, yid, goToNext]);

	// Keep direct video mute status in sync
	useEffect(() => {
		if (videoRef.current) {
			videoRef.current.muted = isMuted;
		}
	}, [isMuted]);

	// Touch gestures for swiping
	const touchStartRef = useRef(0);
	const handleTouchStart = (e: React.TouchEvent) => {
		touchStartRef.current = e.touches[0].clientX;
	};
	const handleTouchEnd = (e: React.TouchEvent) => {
		const touchEnd = e.changedTouches[0].clientX;
		const delta = touchEnd - touchStartRef.current;
		if (Math.abs(delta) > 50) {
			if (delta < 0) goToNext();
			else goToPrev();
		}
	};

	return (
		<div 
			className="fixed inset-0 z-[1000] bg-black/60 backdrop-blur-md flex items-center justify-center" 
			onTouchStart={handleTouchStart} 
			onTouchEnd={handleTouchEnd}
		>
			{/* External Close Button removed */}

			{/* External Left Arrow removed */}

			{/* Story Container (iPhone aspect ratio) */}
			<div className="relative w-full h-[100dvh] md:w-[400px] md:h-[85vh] md:max-h-[850px] bg-black md:rounded-[2rem] overflow-hidden shadow-2xl flex flex-col">
				
				{/* Progress Bars */}
				<div className="absolute top-3 left-3 right-3 flex gap-1 z-50">
					{videos.map((_, i) => (
						<div key={i} className="h-1 bg-white/30 rounded-full flex-1 overflow-hidden">
							<div
								className="h-full bg-white rounded-full transition-all"
								style={{
									width: i < currentIndex ? "100%" : i === currentIndex ? `${progress}%` : "0%",
									transition: i === currentIndex ? "width 0.1s linear" : "none"
								}}
							></div>
						</div>
					))}
				</div>

				{/* Header */}
				<div className="absolute top-6 left-4 right-4 z-50 flex items-center justify-between text-white drop-shadow-md">
					<div className="flex items-center gap-2.5">
						<div className="w-9 h-9 rounded-full bg-gradient-to-tr from-[#ed008c] to-[#ff66b2] flex items-center justify-center p-[2px]">
							<div className="w-full h-full rounded-full bg-white flex items-center justify-center overflow-hidden">
								<img 
									src="/logo.png.webp" 
									alt="Parex" 
									className="w-7 h-7 object-contain" 
									onError={(e) => {
										(e.target as HTMLElement).style.display = 'none';
									}} 
								/>
							</div>
						</div>
						<div>
							<span className="font-semibold text-sm tracking-wide block">Parex Tunisie</span>
						</div>
					</div>
					<div className="flex items-center gap-2">
						{!yid && (
							<button 
								onClick={() => setIsMuted(!isMuted)} 
								className="text-white hover:text-[#ed008c] p-2 transition-colors"
								title={isMuted ? "Unmute" : "Mute"}
							>
								{isMuted ? (
									<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="w-5 h-5">
										<path strokeLinecap="round" strokeLinejoin="round" d="M17.25 9.75L19.5 12m0 0l2.25 2.25M19.5 12l2.25-2.25M19.5 12l-2.25 2.25m-10.5-6L4.5 9H1.5v6h3l4.5 3.75V5.25z" />
									</svg>
								) : (
									<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="w-5 h-5">
										<path strokeLinecap="round" strokeLinejoin="round" d="M19.114 5.636a9 9 0 010 12.728M16.463 8.288a5.25 5.25 0 010 7.424M6.75 8.25l4.72-4.72a.75.75 0 011.28.53v15.88a.75.75 0 01-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.01 9.01 0 012.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75z" />
									</svg>
								)}
							</button>
						)}
						{/* Close button (Internal) */}
						<button 
							onClick={onClose} 
							className="text-white hover:text-[#ed008c] p-2 text-lg font-bold transition-colors bg-black/20 hover:bg-black/40 rounded-full w-8 h-8 flex items-center justify-center backdrop-blur-sm" 
							aria-label="Close story"
						>
							✕
						</button>
					</div>
				</div>

				{/* Video Player Section */}
				<div className="flex-1 w-full h-full flex items-center justify-center bg-black relative group/player">
					{yid ? (
						<iframe
							key={currentIndex}
							className="w-full h-full aspect-[9/16]"
							src={`https://www.youtube.com/embed/${yid}?autoplay=1&mute=${isMuted ? 1 : 0}&rel=0&modestbranding=1&controls=0&showinfo=0`}
							title={`story-video-${currentVideo.id}`}
							allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
						/>
					) : (
						<video
							ref={videoRef}
							key={currentIndex}
							className="w-full h-full object-cover"
							playsInline
							autoPlay
							preload="auto"
						>
							<source src={getImageUrl(currentVideo.link)} type="video/mp4" />
							Votre navigateur ne supporte pas la vidéo.
						</video>
					)}

					{/* Navigation tap overlays for mobile */}
					<div className="absolute inset-y-0 left-0 w-1/4 z-30 cursor-pointer md:hidden" onClick={goToPrev}></div>
					<div className="absolute inset-y-0 right-0 w-1/4 z-30 cursor-pointer md:hidden" onClick={goToNext}></div>

					{/* Left Arrow (Desktop only, inside video) */}
					<button 
						onClick={goToPrev} 
						className="absolute left-2 top-1/2 -translate-y-1/2 text-white/70 hover:text-[#ed008c] z-50 p-2 bg-black/20 hover:bg-black/50 rounded-full transition-all hidden md:flex items-center justify-center backdrop-blur-sm opacity-0 group-hover/player:opacity-100" 
						aria-label="Previous story"
					>
						<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="3" stroke="currentColor" className="w-5 h-5">
							<path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
						</svg>
					</button>

					{/* Right Arrow (Desktop only, inside video) */}
					<button 
						onClick={goToNext} 
						className="absolute right-2 top-1/2 -translate-y-1/2 text-white/70 hover:text-[#ed008c] z-50 p-2 bg-black/20 hover:bg-black/50 rounded-full transition-all hidden md:flex items-center justify-center backdrop-blur-sm opacity-0 group-hover/player:opacity-100" 
						aria-label="Next story"
					>
						<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="3" stroke="currentColor" className="w-5 h-5">
							<path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
						</svg>
					</button>
				</div>

				{/* Product Link Sticker (Instagram Style) */}
				{currentVideo.product && (
					<div className="absolute bottom-[80px] left-1/2 -translate-x-1/2 z-40 w-max max-w-[90%]">
						<Link
							href={`/produit/${encodeURIComponent(String(currentVideo.product.slug || currentVideo.product.id))}`}
							className="bg-white/95 backdrop-blur-md text-black p-2 pr-4 rounded-xl flex items-center gap-3 shadow-[0_4px_25px_rgba(0,0,0,0.5)] transition-transform hover:scale-105 active:scale-95"
						>
							{currentVideo.product.images?.[0] ? (
								<img
									src={getImageUrl(currentVideo.product.images[0])}
									alt={currentVideo.product.name}
									className="w-10 h-10 rounded-md object-cover bg-gray-100 shrink-0 border border-gray-200/50"
								/>
							) : (
								<div className="w-10 h-10 rounded-md bg-gray-100 shrink-0 flex items-center justify-center border border-gray-200/50">
									<svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
									</svg>
								</div>
							)}
							<div className="flex flex-col min-w-0 justify-center">
								<span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-0.5">Voir le produit</span>
								<span className="text-sm font-bold truncate text-gray-900 max-w-[150px] leading-none">
									{currentVideo.product.name}
								</span>
							</div>
							<div className="ml-1 text-gray-400">
								<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor" className="w-4 h-4">
									<path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
								</svg>
							</div>
						</Link>
					</div>
				)}
			</div>

			{/* External Right Arrow removed */}
		</div>
	);
};

export default function PinkStuffUsage() {
	const [emblaRef, emblaApi] = useEmblaCarousel({ align: "start", loop: false, skipSnaps: false });
	const [sharedVideos, setSharedVideos] = useState<SharedVideoItem[]>([]);
	const [loading, setLoading] = useState(true);
	
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [activeVideoIndex, setActiveVideoIndex] = useState(0);

	// Upload Modal State
	const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
	const [isUploading, setIsUploading] = useState(false);
	const [uploadFile, setUploadFile] = useState<File | null>(null);
	const [uploadProductId, setUploadProductId] = useState<string>("");
	const [products, setProducts] = useState<any[]>([]);

	const fetchSharedVideos = useCallback(async () => {
		try {
			setLoading(true);
			const base = API_BASE_URL || "";
			const res = await fetch(`${base}/shared-videos?includeProduct=true`, { cache: 'no-store' });
			if (!res.ok) throw new Error("Failed to fetch shared videos");
			const data = await res.json();
			const list: SharedVideoItem[] = Array.isArray(data) ? data : (data.items || data.data || []);
			setSharedVideos(list || []);
		} catch (err) {
			console.error('Failed to load shared videos', err);
			setSharedVideos([]);
		} finally {
			setLoading(false);
		}
	}, []);

	// Fetch products when modal opens
	useEffect(() => {
		if (isUploadModalOpen && products.length === 0) {
			api.get('/products').then(res => {
				const items = Array.isArray(res.data) ? res.data : (res.data.items || res.data.data || []);
				setProducts(items);
			}).catch(err => console.error("Failed to load products", err));
		}
	}, [isUploadModalOpen, products.length]);

	const handleUploadSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!uploadFile || !uploadProductId) return;

		setIsUploading(true);
		try {
			const formData = new FormData();
			formData.append('file', uploadFile);

			const uploadRes = await api.post('/shared-videos/upload', formData, {
				headers: { 'Content-Type': 'multipart/form-data' }
			});

			const fileUrl = uploadRes.data.url;

			await api.post('/shared-videos', {
				productId: uploadProductId,
				link: fileUrl
			});

			setIsUploadModalOpen(false);
			setUploadFile(null);
			setUploadProductId("");
			fetchSharedVideos();
		} catch (err) {
			console.error("Upload error:", err);
			alert("Une erreur est survenue lors de l'envoi de la vidéo.");
		} finally {
			setIsUploading(false);
		}
	};

	const scrollPrev = useCallback(() => { if (emblaApi) emblaApi.scrollPrev(); }, [emblaApi]);
	const scrollNext = useCallback(() => { if (emblaApi) emblaApi.scrollNext(); }, [emblaApi]);

	const openModal = (index: number) => {
		setActiveVideoIndex(index);
		setIsModalOpen(true);
	};

	const closeModal = () => {
		setIsModalOpen(false);
	};

	// Prevent scrolling when story modal is open
	useEffect(() => {
		if (isModalOpen) {
			document.body.style.overflow = 'hidden';
		} else {
			document.body.style.overflow = '';
		}
		return () => {
			document.body.style.overflow = '';
		};
	}, [isModalOpen]);

	useEffect(() => {
		fetchSharedVideos();
	}, [fetchSharedVideos]);

	return (
		<section className="max-w-6xl mx-auto px-4 py-16 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start overflow-hidden">
			<div className="relative z-10 space-y-6 lg:col-span-4">
				<h3 className="text-4xl md:text-5xl font-extrabold text-[#ed008c] leading-tight">La vie est facile avec Parex : du nettoyage au goût</h3>
				<p className="text-gray-600">Vidéos partagées par les utilisateurs et produits associés.</p>
				<div className="flex flex-wrap gap-4 mt-6">
					<Link href="/nos-produits" className="inline-flex items-center gap-2 rounded-full bg-main px-6 py-3 text-sm font-semibold text-white shadow-lg transition-all hover:scale-105">
						Voir nos produits
					</Link>
					 
				</div>
			</div>

			<div className="relative group lg:col-span-8">
				<div className="overflow-hidden" ref={emblaRef}>
					<div className="flex ml-[-16px]">
						{loading ? (
							<div className="p-6 text-center text-gray-500">Chargement des vidéos...</div>
						) : sharedVideos.length === 0 ? (
							<div className="p-6 text-gray-400">Aucune vidéo partagée</div>
						) : (
							sharedVideos.map((sv, index) => {
								const yid = extractYouTubeId(sv.link) || undefined;
								return (
									<div 
										key={sv.id} 
										className="flex-[0_0_80%] md:flex-[0_0_40%] min-w-0 pl-4 relative cursor-pointer"
										onClick={() => openModal(index)}
									>
										<div className="relative aspect-[9/16] rounded-2xl overflow-hidden bg-black shadow-lg group/card">
											{yid ? (
												<iframe
													className="w-full h-full pointer-events-none"
													src={`https://www.youtube.com/embed/${yid}?rel=0&modestbranding=1&controls=0&mute=1`}
													title={`video-${sv.id}`}
													allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
												/>
											) : (
												<video
													className="w-full h-full object-cover pointer-events-none"
													src={getImageUrl(sv.link)}
													playsInline
													muted
													autoPlay
													loop
													preload="metadata"
													crossOrigin="anonymous"
												/>
											)}

											{/* Play Icon Overlay */}
											<div className="absolute inset-0 bg-black/10 group-hover/card:bg-black/35 transition-all flex items-center justify-center">
												<span className="w-12 h-12 bg-white/20 text-white rounded-full flex items-center justify-center shadow-lg backdrop-blur-md transition-all group-hover/card:scale-110 group-hover/card:bg-white/40">
													<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24" className="w-6 h-6 text-white ml-0.5">
														<path d="M8 5v14l11-7z" />
													</svg>
												</span>
											</div>

											{/* Product thumbnail (if included) - clickable to product page */}
											{sv.product?.images?.[0] && (
												<div 
													onClick={(e) => e.stopPropagation()} 
													className="absolute bottom-3 right-3 w-1/4 z-20 hover:scale-105 transition-transform"
												>
													<Link href={`/produit/${encodeURIComponent(String(sv.product?.slug || sv.product?.id))}`}>
														<img 
															src={getImageUrl(sv.product.images[0])} 
															alt={sv.product.name} 
															className="w-full h-auto drop-shadow-md rounded-lg border border-white/20 bg-white" 
														/>
													</Link>
												</div>
											)}
										</div>
									</div>
								);
							})
						)}
					</div>
				</div>

				<button onClick={scrollPrev} aria-label="Previous video" className="absolute left-[-10px] top-1/2 -translate-y-1/2 z-20 hover:scale-110 transition-transform hidden md:block">
					<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="w-10 h-10 text-white bg-black/40 rounded-full p-1">
						<path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M15 18l-6-6 6-6" />
					</svg>
				</button>

				<button onClick={scrollNext} aria-label="Next video" className="absolute right-[-10px] top-1/2 -translate-y-1/2 z-20 hover:scale-110 transition-transform hidden md:block">
					<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="w-10 h-10 text-white bg-black/40 rounded-full p-1">
						<path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M9 6l6 6-6 6" />
					</svg>
				</button>
			</div>

			{/* Instagram Story-style Full-screen Modal */}
			{isModalOpen && (
				<StoryModal
					videos={sharedVideos}
					startIndex={activeVideoIndex}
					onClose={closeModal}
				/>
			)}

			{/* Upload Modal */}
			{isUploadModalOpen && (
				<div className="fixed inset-0 z-[1050] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
					<div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
						<div className="bg-gray-50 px-6 py-4 border-b flex items-center justify-between">
							<h3 className="text-xl font-bold text-gray-800">Partager une vidéo</h3>
							<button onClick={() => setIsUploadModalOpen(false)} className="text-gray-400 hover:text-gray-600 p-1">
								✕
							</button>
						</div>
						<form onSubmit={handleUploadSubmit} className="p-6 space-y-5">
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">Produit associé *</label>
								<select
									required
									value={uploadProductId}
									onChange={(e) => setUploadProductId(e.target.value)}
									className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ed008c] focus:border-[#ed008c] bg-white outline-none"
								>
									<option value="">Sélectionnez un produit...</option>
									{products.map(p => (
										<option key={p.id} value={p.id}>{p.name}</option>
									))}
								</select>
							</div>
							
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">Fichier vidéo (MP4, etc.) *</label>
								<input
									type="file"
									accept="video/*"
									required
									onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
									className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ed008c] focus:border-[#ed008c] outline-none file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-pink-50 file:text-[#ed008c] hover:file:bg-pink-100"
								/>
							</div>

							<div className="pt-2">
								<button
									type="submit"
									disabled={isUploading || !uploadFile || !uploadProductId}
									className="w-full bg-[#ed008c] text-white font-bold py-3 px-4 rounded-xl shadow-lg hover:bg-[#d0007b] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
								>
									{isUploading ? (
										<>
											<svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
												<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
												<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
											</svg>
											Envoi en cours...
										</>
									) : (
										"Publier la vidéo"
									)}
								</button>
							</div>
						</form>
					</div>
				</div>
			)}
		</section>
	);
}

